"use client";

import React, { useMemo, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Button,
  Card,
  CardBody,
  Tabs,
  Tab,
  Spinner,
  Avatar
} from "@heroui/react";
import { Typography } from "../../../../components";
import { staffApi, Creator } from "../../../../api/staff";
import { refShareApi } from "../../../../api/services";
import { useTranslations } from "../../../../contexts/translation.context";
import { usePageBreadcrumbs } from "../../../../hooks/usePageBreadcrumbs";
import {
  GeneralInfoTab,
  BalanceHistoryTab,
  ModelsTab,
  TransactionsTab,
  StatisticsTab,
  CreatorRevShareOffersTab,
} from "./components";
import { getCreatorStatusConfig, getCreatorLevelConfig } from "../constants";

interface CreatorDetailsPageProps {}

/**
 * CreatorDetailsPage Component
 *
 * Страница деталей криейтора в административной панели.
 * Использует структуру из onlyu_front/account-page, адаптированную под onlyu_admin архитектуру.
 *
 * Основные особенности:
 * - Использует единый API client через staffApi
 * - Следует паттерну unified components для страниц и API
 * - Интегрируется с HeroUI компонентами
 * - Обеспечивает навигацию по табам через URL
 */
const CreatorDetailsPage: React.FC<CreatorDetailsPageProps> = () => {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const navigate = useNavigate();
  const { t } = useTranslations();

  // State
  const [creatorData, setCreatorData] = useState<Creator | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [balance, setBalance] = useState<string>("0");
  const [accountsCount, setAccountsCount] = useState<number>(0);
  const [revShareCount, setRevShareCount] = useState<number>(0);
  const [offersCount, setOffersCount] = useState<number>(0);
  const [cplActive, setCplActive] = useState<number>(0);
  const [cplTotal, setCplTotal] = useState<number>(0);

  // Определяем активный таб из URL или устанавливаем по умолчанию
  const activeTab = tab || "balance";

  // Валидные табы
  const validTabs = ["balance", "accounts", "offers", "revshare", "cpl"];

  // Breadcrumbs
  const breadcrumbs = useMemo(() => [
    { label: t('sidebar.dashboard'), href: '/dashboard' },
    { label: t('sidebar.creators'), href: '/users/creators' },
    { label: creatorData?.name || '...', isActive: true }
  ], [t, creatorData?.name]);

  usePageBreadcrumbs(breadcrumbs);

  // Fetch creator data
  const fetchCreatorData = async () => {
    if (!id) {
      setError("Creator ID is required");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await staffApi.getCreator(id);
      setCreatorData(data);
    } catch (error: any) {
      console.error("Error fetching creator data:", error);
      setError(error?.response?.data?.message || "Failed to load creator data");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCreatorData();
    fetchCounts();
  }, [id]);

  // Fetch counts for badges
  const fetchCounts = async () => {
    if (!id) return;

    try {
      // Fetch balance
      const balanceResponse = await staffApi.getCreatorBalance(id);
      setBalance(balanceResponse.balance);

      // Fetch accounts count
      const accountsResponse = await staffApi.getCreatorModels(id, {
        page: 1,
        page_size: 1,
      });
      setAccountsCount(accountsResponse.count);

      // Fetch RevShare offers count
      const offersResponse = await refShareApi.getOffers({
        page: 1,
        page_size: 1,
        owner: id,
      });
      setOffersCount(offersResponse.count || 0);

      // Fetch RevShare campaigns count
      const revshareResponse = await refShareApi.getOrders({
        page: 1,
        page_size: 1,
        client: id,
      });
      setRevShareCount(revshareResponse.count || 0);

      // Fetch CPL orders count
      const cplResponse = await staffApi.getCreatorCPLOrders(id, {
        page: 1,
        page_size: 1,
      });
      setCplTotal(cplResponse.count);

      // Fetch active CPL orders count
      const cplActiveResponse = await staffApi.getCreatorCPLOrders(id, {
        page: 1,
        page_size: 1,
        status: 'in_progress',
      });
      setCplActive(cplActiveResponse.count);
    } catch (error) {
      console.error("Error fetching counts:", error);
    }
  };

  // Проверяем валидность таба и перенаправляем на дефолтный если нужно
  useEffect(() => {
    if (tab && !validTabs.includes(tab)) {
      navigate(`/users/creators/${id}/balance`, { replace: true });
    } else if (!tab) {
      navigate(`/users/creators/${id}/balance`, { replace: true });
    }
  }, [tab, id, navigate]);

  const handleTabChange = (key: string) => {
    navigate(`/users/creators/${id}/${key}`);
  };

  const handleBackToList = () => {
    navigate('/users/creators');
  };

  // Статистические карточки
  const StatCard = ({ title, value, className = "" }: { title: string; value: string | number; className?: string }) => (
    <Card className={`w-full border-0 rounded-none shadow-none ${className}`}>
      <CardBody className="px-[16px] py-[12px]">
        <Typography variant="body2" className="text-[#52525b] mb-[4px] text-[14px] leading-[18px] font-medium">
          {title}
        </Typography>
        <Typography variant="body3" className="text-[#3f3f46] text-[14px] break-words">
          {value}
        </Typography>
      </CardBody>
    </Card>
  );

  // Форматирование даты
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получить конфиг статуса из constants
  const getStatusConfig = (status: Creator["status"]) => {
    return getCreatorStatusConfig(status, t);
  };

  // Получить конфиг уровня из constants
  const getLevelConfig = (lvl: Creator["lvl"]) => {
    return getCreatorLevelConfig(lvl, t);
  };

  // Получить цвет уровня
  const getLevelColor = (lvl: string) => {
    switch (lvl) {
      case 'diamond':
        return { bg: '#B9F2FF', text: '#000000' };
      case 'platinum':
        return { bg: '#18181B', text: '#FFFFFF' };
      case 'gold':
        return { bg: '#F5A524', text: '#FFFFFF' };
      case 'silver':
        return { bg: '#71717A', text: '#FFFFFF' };
      case 'bronze':
        return { bg: '#CD7F32', text: '#FFFFFF' };
      default:
        return { bg: '#F4F4F5', text: '#71717A' };
    }
  };

  // Компонент для заголовка таба с бейджем
  const TabTitle = ({ label, badge, isGreen = false }: { label: string; badge?: string | number; isGreen?: boolean }) => (
    <div className="flex items-center gap-2">
      <span>{label}</span>
      {badge !== undefined && (
        <span
          className={`inline-flex h-[20px] min-w-[28px] items-center justify-center rounded-full px-[8px] text-[12px] font-medium ${
            isGreen
              ? 'bg-[#17C964]/10 text-[#17C964]'
              : 'bg-[#D4D4D8]/40 text-[#71717A]'
          }`}
        >
          {badge}
        </span>
      )}
    </div>
  );

  // Контент табов
  const renderTabContent = () => {
    if (!creatorData) return null;

    switch (activeTab) {
      case "balance":
        return <BalanceHistoryTab creatorId={id!} />;

      case "accounts":
        return <ModelsTab creatorId={id!} />;

      case "offers":
        return <CreatorRevShareOffersTab creatorId={id!} />;

      case "revshare":
        return <TransactionsTab creatorId={id!} />;

      case "cpl":
        return <StatisticsTab creatorId={id!} />;

      default:
        return <BalanceHistoryTab creatorId={id!} />;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="lg" color="primary" />
      </div>
    );
  }

  // Error state
  if (error || !creatorData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <Typography variant="h5" className="text-danger">
          {error || "Creator not found"}
        </Typography>
        <Button color="primary" onPress={handleBackToList}>
          {t('admin.creators.backToList')}
        </Button>
      </div>
    );
  }
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  return (
    <div className="flex flex-col">
      <div className="px-6 py-5 flex flex-col">
        <div className="mb-[32px] flex items-center gap-4">
          <Typography variant="h5" className="text-[#11181C] font-semibold">
            {creatorData.name}
          </Typography>
        </div>
        <div className="flex flex-col xl:flex-row gap-6">
          <div className="w-full flex flex-row items-start gap-[14px] xl:w-56 xl:flex-col flex-shrink-0">
            <div className="mb-6 flex flex-col gap-4 xl:flex-col  xl:gap-0">
              <div className="w-[200px] h-[200px] flex-shrink-0 rounded-xl overflow-hidden flex flex-row xl:mb-[16px] bg-gray-100 flex items-center justify-center">
                <Avatar
                  src={creatorData.avatar}
                  alt={creatorData.name}
                  showFallback
                  name={getInitials(creatorData.name)}
                  size="sm"
                  className="w-full h-full rounded-[12px] bg-gray-200"
                  classNames={{
                    fallback: "bg-gray-200 text-gray-600"
                  }}
                />
              </div>
              <div className="flex-1 max-w-[200px] xl:mb-4 flex flex-row !flex-wrap items-start xl:flex-col gap-2">
                <span
                  className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium capitalize w-fit"
                  style={{
                    backgroundColor: creatorData.type === 'agency' ? '#7828C8' + '20' : '#006FEE' + '20',
                    color: creatorData.type === 'agency' ? '#7828C8' : '#006FEE'
                  }}
                >
                  {creatorData.type === 'agency'
                    ? (t('admin.creators.type.agency') || 'Агентство')
                    : (t('admin.creators.type.creator') || 'Криейтор')
                  }
                </span>
                  <span
                    className="inline-flex items-center justify-center rounded-lg px-[8px] py-[2px] text-[11px] font-medium whitespace-nowrap"
                    style={{
                      backgroundColor: `${getStatusConfig(creatorData.status).color}20`,
                      color: getStatusConfig(creatorData.status).color
                    }}
                  >
                    {getStatusConfig(creatorData.status).label}
                  </span>
                  <span
                    className="inline-flex items-center justify-center rounded-lg px-[6px] py-[2px] text-[11px] font-medium capitalize w-fit"
                    style={{
                      backgroundColor: getLevelColor(creatorData.lvl).bg,
                      color: getLevelColor(creatorData.lvl).text
                    }}
                  >
                    {getLevelConfig(creatorData.lvl).label}
                  </span>
              </div>
            </div>
            <div className="flex flex-col gap-[14px]">
              <div className="flex flex-col border border-[#E5E5E5] rounded-[14px] overflow-hidden xl:mb-4">
                <StatCard
                  title={t('admin.creators.details.email') || 'Email'}
                  value={creatorData.email || '—'}
                />
                <div className="border-t !border-[#E5E5E5]"></div>
                <StatCard
                  title={t('admin.creators.details.telegram') || 'Telegram'}
                  value={creatorData.tg_tag || '—'}
                />
                <div className="border-t !border-[#E5E5E5]"></div>
                <StatCard
                  title={t('admin.creators.details.whatsapp') || 'WhatsApp'}
                  value={creatorData.whats_app || '—'}
                />
              </div>

              {/* Additional Info Cards */}
              <div className="flex flex-col border border-[#E5E5E5] rounded-[14px] overflow-hidden">
                <StatCard
                  title={t('admin.creators.details.createdAt') || 'Дата создания'}
                  value={formatDate(creatorData.created_at)}
                />
              </div>
            </div>
          </div>

          {/* Right Content */}
          <div className="flex-1 flex flex-col">
            {/* Tabs */}
            <div className="mb-[32px]">
              <div className="bg-[#f4f4f5] rounded-xl w-fit">
                <Tabs
                  selectedKey={activeTab}
                  onSelectionChange={(key: any) => handleTabChange(key as string)}
                  variant="light"
                  classNames={{
                    tabList: "gap-1",
                    cursor: "bg-white rounded-lg shadow-sm",
                    tab: "px-[12px] py-[4px] text-sm font-normal data-[selected=true]:text-black text-[#71717a]",
                    tabContent: "group-data-[selected=true]:text-black"
                  }}
                >
                  <Tab
                    key="balance"
                    title={
                      <TabTitle
                        label={t('admin.creators.details.tabs.balance')}
                        badge={`$${parseFloat(balance).toFixed(2)}`}
                        isGreen={true}
                      />
                    }
                  />
                  <Tab
                    key="accounts"
                    title={
                      <TabTitle
                        label={t('admin.creators.details.tabs.accounts')}
                        badge={accountsCount}
                      />
                    }
                  />
                  <Tab
                    key="offers"
                    title={
                      <TabTitle
                        label={t('admin.creators.details.tabs.offers')}
                        badge={offersCount}
                      />
                    }
                  />
                  <Tab
                    key="revshare"
                    title={
                      <TabTitle
                        label={t('admin.creators.details.tabs.revshare')}
                        badge={revShareCount}
                      />
                    }
                  />
                  <Tab
                    key="cpl"
                    title={
                      <TabTitle
                        label={t('admin.creators.details.tabs.cpl')}
                        badge={cplTotal > 0 ? `${cplActive}/${cplTotal}` : cplTotal}
                      />
                    }
                  />
                </Tabs>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1">
              {renderTabContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatorDetailsPage;
